/**
 * Plan-limit lines for the pricing and upgrade screens.
 *
 * Every number and every rule here comes from `@beheard/limits` — the backend's
 * own plan tables and limit copy, the ones its enforcement and refusal messages
 * use — so no screen can state a limit the server does not apply.
 */
import {
  LIMIT_COPY,
  PLAN_CAPABILITIES,
  PLAN_POLICIES,
  PLAN_POLICY_KEY_BY_TIER,
  USAGE_ALLOWANCE_RULES,
  getUsageAllowanceRule,
  limitSummary,
  periodRate,
  usageActionCopy,
} from '@beheard/limits';

export type AllowanceTier = 'free' | 'pro' | 'pro_ai';

/** "Practice sessions: 3 per day (rolling 24 hours) and 60 per calendar month". */
function practiceSessionsLine(daily: number, monthly: number): string {
  return `${limitSummary('maxDailyPracticeSessions', daily)} and ${monthly} ${periodRate(LIMIT_COPY.maxMonthlyPracticeSessions.period)}`;
}

/**
 * A plan's enforced session-count limits (PlanLimitService), one line each:
 *   Practice sessions: 3 per day (rolling 24 hours) and 60 per calendar month
 *   NPC battles: 5 per day (rolling 24 hours), a separate limit from practice sessions
 * The tier is mapped to its PLAN_POLICIES key by PLAN_POLICY_KEY_BY_TIER
 * (Pro → pro_individual; Pro + AI → pro_ai, which has Pro's session limits).
 */
export function planSessionLimitLines(tier: AllowanceTier): string[] {
  const limits = PLAN_POLICIES[PLAN_POLICY_KEY_BY_TIER[tier]];
  const lines: string[] = [];
  if (!limits) return lines;
  if (limits.maxDailyPracticeSessions !== undefined && limits.maxMonthlyPracticeSessions !== undefined) {
    lines.push(practiceSessionsLine(limits.maxDailyPracticeSessions, limits.maxMonthlyPracticeSessions));
  }
  if (limits.maxDailyNpcBattles !== undefined) {
    lines.push(`${limitSummary('maxDailyNpcBattles', limits.maxDailyNpcBattles)}, a separate limit from practice sessions`);
  }
  return lines;
}

/**
 * The Free plan's enforced limits, as one line each:
 *   Practice sessions: 3 per day (rolling 24 hours) and 60 per calendar month
 *   NPC battles: 5 per day (rolling 24 hours), a separate limit from practice sessions
 *   Voice analyses: 2 per day and 10 per calendar month
 */
export function freePlanLimitLines(): string[] {
  const lines = planSessionLimitLines('free');
  const voice = getUsageAllowanceRule('voice_practice_classic');
  if (voice?.freeTierLimitPerDay !== undefined && voice.freeTierLimitPerMonth !== undefined) {
    lines.push(
      `${usageActionCopy('voice_practice_classic').title}: ${voice.freeTierLimitPerDay} per day and ${voice.freeTierLimitPerMonth} ${periodRate('calendar_month')}`
    );
  }
  return lines;
}

/**
 * The actions metered against the PRACTICE pool (USAGE_ALLOWANCE_RULES:
 * allowancePool 'practice', allowanceUnits > 0) — exactly the ones
 * UsageService.hasUnlimitedAllowance lets an `unlimitedPractice` plan perform
 * without a pool check or a reservation.
 */
export function practicePoolActions(): string[] {
  return USAGE_ALLOWANCE_RULES.filter((c) => c.allowancePool === 'practice' && c.allowanceUnits > 0).map((c) => c.action);
}

const listJoin = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * The line for a plan with `unlimitedPractice` (PLAN_CAPABILITIES): what it
 * means as enforced — those actions are not metered per use — and what it does
 * not mean: session limits (PlanLimitService) still apply. Deliberately never
 * the word "unlimited": session limits apply on every plan, so the pricing page
 * makes no unlimited claim (e2e/specs/plan-limits.spec.ts).
 */
export function unlimitedPracticeLine(): string {
  const list = listJoin(practicePoolActions().map((a) => usageActionCopy(a).noun));
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} aren't metered per use on this plan — session limits still apply`;
}

/**
 * A tier's monthly usage allowances (a LOCAL NONCOMMERCIAL SAFETY LIMIT, never
 * credits: nothing here is priced or sold), e.g.
 * "Monthly practice allowance: 5 units (a voice analysis uses 1)".
 */
export function allowanceLines(tier: AllowanceTier): string[] {
  const cap = PLAN_CAPABILITIES.find((p) => p.planKey === tier);
  if (!cap) return [];
  const voiceUnits = getUsageAllowanceRule('voice_practice_classic')?.allowanceUnits;
  const lines: string[] = [];
  if (cap.unlimitedPractice) {
    lines.push(unlimitedPracticeLine());
  } else if (cap.monthlyAllowance.practice > 0) {
    lines.push(
      `Monthly practice allowance: ${cap.monthlyAllowance.practice} units${voiceUnits ? ` (a voice analysis uses ${voiceUnits})` : ''}`
    );
  }
  if (cap.monthlyAllowance.coaching > 0) {
    lines.push(`Monthly AI voice coaching allowance: ${cap.monthlyAllowance.coaching} units`);
  }
  return lines;
}

/** The pricing page's footnote: allowances and session limits are different things, and neither is bought. */
export const LIMITS_FOOTNOTE =
  'Monthly allowances cap metered actions such as voice analyses; they are usage limits, not credits, and cannot be bought. Session limits are separate and apply on every plan. Your current limits and usage are on the Usage & Limits page.';
